import { useEffect, useState } from 'react';
import { Header3 } from 'components/UI/Brand/text';
import { Document } from '@contentful/rich-text-types';
import CustomImage from 'components/UI/CustomImage/CustomImage';
import CustomLink from 'containers/Learn/CustomLink';
import { MediaTypes } from 'containers/Training/Layouts/MediaTypes';
import { IGeneralSection } from 'interfaces/IMarketing';
import AccordionItem from 'components/UI/Accordion/AccordionItem';
import ArrowSearchLink from 'components/UI/Links/ArrowSearchLink';
import ContentfulRenderer from 'components/ContentfulRenderer';
import { AlertBox } from 'components';
import { ImageCroppings } from './ImageCroppings';
import { HEADER_SIZE } from './utils/getHeader.util';
import styles from './styles.module.scss';

interface IBottomSectionAProps {
  article: IGeneralSection;
}

interface CollapsibleObject {
  question: string;
  answer: Document;
}

const BottomSectionA = ({ article }: IBottomSectionAProps) => {
  const [imageWidth, setImageWidth] = useState(0);
  const [imageHeight, setImageHeight] = useState(0);
  const [isCollapsible, setIsCollapsible] = useState(false);
  const [collapseObject, setCollapseObject] = useState({} as CollapsibleObject);

  useEffect(() => {
    if (article.croppingSize === ImageCroppings.Tall) {
      setImageWidth(752);
      setImageHeight(1337);
    } else if (article.croppingSize === ImageCroppings.Wide) {
      setImageWidth(752);
      setImageHeight(423);
    } else if (article.croppingSize === ImageCroppings.Square) {
      setImageWidth(752);
      setImageHeight(752);
    }
  }, []);

  useEffect(() => {
    const { title, longDescription } = article;
    if (article.imageAlignment === 'Collapsible text') {
      const collapsibleObject = {
        question: title,
        answer: longDescription,
      };
      setCollapseObject(collapsibleObject);
      setIsCollapsible(true);
    }
  }, []);

  return (
    <>
      {isCollapsible && collapseObject.question && collapseObject.answer ? (
        <AccordionItem article={collapseObject} />
      ) : (
        <div className={styles.bottomSectionA} id={article.title}>
          {article.title && (
            <Header3 as="h2" className={styles.title}>
              {article.title}
            </Header3>
          )}
          {article.longDescription && (
            <ContentfulRenderer
              highestHeadingSize={HEADER_SIZE.H4}
              highestDOMHeadingSize={HEADER_SIZE.H3}
            >
              {article.longDescription}
            </ContentfulRenderer>
          )}
          <div>
            {article.media && article.mediaType === MediaTypes.IMAGE && (
              <CustomImage
                className={styles.image}
                src={article.media}
                alt={article.title}
                width={imageWidth}
                height={imageHeight}
              />
            )}
            {article.media && article.mediaType === MediaTypes.VIDEO && (
              <video
                controls
                style={{ width: '100%', height: '100%', outline: 'none' }}
                src={article.media}
                preload="auto"
              />
            )}
          </div>
          {article.infoBox && (
            <AlertBox type="info" OverrideIcon=" " className={styles.infoBox}>
              <ContentfulRenderer highestHeadingSize={HEADER_SIZE.H4}>
                {article.infoBox}
              </ContentfulRenderer>
            </AlertBox>
          )}
          {article.link &&
            (article.link.type === 'hyperlink' ||
              article.link.type === 'articleContainerLink') && (
              <CustomLink
                text={article.link.title}
                link={
                  article.link.type === 'hyperlink'
                    ? article.link.hyperlink
                    : article.link.link
                }
                type={article.link.type}
              />
            )}
          {article.link && article.link.type === 'search criteria' && (
            <div className={styles.linkWithArrowContainer}>
              <ArrowSearchLink link={article.link} />
            </div>
          )}
        </div>
      )}
    </>
  );
};

export default BottomSectionA;
